import { Component, h, Prop, Element, State, Build, Host } from '@stencil/core';

const COMPONENT_NAME = 'sn-reviews-decoration';
const BASE_IMAGE_URL = 'https://marketing-static.signnow.com/349/images/hippa';

/**
 * Animated illustrations frame component that displays multiple absolutely positioned images
 * with viewport-triggered animations. Designed to be used as a decoration slot inside
 * sn-testimonials-type-1. Images animate sequentially when the container enters the viewport.
 * Default 10 image positions are used when no items prop is provided.
 *
 * @product signnow
 * @category SignNow
 * @source reviews
 * @version 2.0.0
 * @example
 *   <sn-testimonials-type-1>
 *     <me-heading slot="heading" variant="2" tag="h2" align="center">What our customers say</me-heading>
 *     <me-text slot="subtitle" variant="medium" align="center">Read testimonials from our satisfied customers</me-text>
 *     <div slot="item"
 *       data-header="Excellent resource"
 *       data-stars="5"
 *       data-author="John Doe"
 *       data-review-raw-html="&lt;p&gt;Great product!&lt;/p&gt;"
 *       data-platform-href="https://example.com/review"
 *       data-platform-href-title="Read full review"
 *     ></div>
 *     <sn-reviews-decoration-type-1 slot="decoration"></sn-reviews-decoration-type-1>
 *   </sn-testimonials-type-1>
 */
@Component({
  tag: 'sn-reviews-decoration-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnReviewsDecoration {
  @Element() el: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Array of item configurations with positioning and animation settings.
   * If not provided, default items will be used.
   * @type {array}
   * @items.type {object}
   * @items.property {string} id - Unique identifier for the image (required)
   * @items.property {string} animationType - Animation type: 'fade-up', 'fade-right', 'fade-left' (default: 'fade-up')
   * @items.property {number} animationDelay - Animation delay in milliseconds (default: 0)
   * @items.property {number} animationDuration - Animation duration in milliseconds (default: 1000)
   * @items.required id
   */
  @Prop({ mutable: true }) items?: any[];

  /**
   * Threshold for IntersectionObserver (0.0 to 1.0)
   * @default 0.2
   */
  @Prop() threshold?: number;

  /**
   * Root margin for IntersectionObserver (e.g., '100px', '50%')
   * @default "100px"
   */
  @Prop() rootMargin?: string;

  @State() isVisible: boolean = false;

  private observer?: IntersectionObserver;
  private animationTimeouts: number[] = [];
  private isParsed = false;

  private getDefaultItems() {
    return [
      { id: '01', animationType: 'fade-up', animationDelay: 500 },
      { id: '02', animationType: 'fade-up', animationDelay: 700 },
      { id: '03', animationType: 'fade-right', animationDelay: 800 },
      { id: '04', animationType: 'fade-left', animationDelay: 500 },
      { id: '05', animationType: 'fade-up', animationDelay: 100 },
      { id: '06', animationType: 'fade-up', animationDelay: 900 },
      { id: '07', animationType: 'fade-up', animationDelay: 1000 },
      { id: '08', animationType: 'fade-up', animationDelay: 1500 },
      { id: '09', animationType: 'fade-up', animationDelay: 1200 },
      { id: '10', animationType: 'fade-right', animationDelay: 1500 },
    ];
  }

  private getItems() {
    if (this.items && Array.isArray(this.items) && this.items.length > 0) {
      return this.items;
    }
    return this.getDefaultItems();
  }

  private getThreshold() {
    return this.threshold !== undefined ? this.threshold : 0.2;
  }

  private getRootMargin() {
    return this.rootMargin !== undefined ? this.rootMargin : '100px';
  }

  parseItems() {
    // If items is not provided, use defaults (no parsing needed)
    if (!this.items) {
      return;
    }

    // Prevent infinite loop: if items is already an array and parsed, skip
    if (this.isParsed && Array.isArray(this.items)) {
      return;
    }

    let rawItems: any[] = [];

    // Handle items that come as string (from HTML attributes during hydration)
    if (Array.isArray(this.items)) {
      rawItems = this.items;
    } else if (typeof this.items === 'string') {
      try {
        rawItems = JSON.parse((this.items as string).replace(/&quot;/g, '"'));
      } catch (e) {
        console.error('Failed to parse items:', e);
        rawItems = [];
      }
    } else {
      // Fallback: try to get from attribute
      const itemsAttr = this.el.getAttribute('items');
      if (itemsAttr) {
        try {
          rawItems = JSON.parse(itemsAttr.replace(/&quot;/g, '"'));
        } catch (e) {
          console.error('Failed to parse items from attribute:', e);
          rawItems = [];
        }
      }
    }

    // Only update if different to prevent infinite loop
    const parsedItems = Array.isArray(rawItems) ? rawItems : [];
    if (!this.isParsed || !Array.isArray(this.items) || this.items.length !== parsedItems.length) {
      this.isParsed = true;
      this.items = parsedItems;
    }
  }

  componentWillLoad() {
    this.parseItems();
  }

  componentDidLoad() {
    // Re-parse in case items were set after componentWillLoad
    this.parseItems();
    this.setupObserver();
  }

  componentDidUpdate() {
    // Re-setup observer if items changed
    if (this.observer) {
      this.observer.disconnect();
    }
    this.setupObserver();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.animationTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  private getImageConfig(id: string) {
    // Hardcoded image mapping based on ID
    const imageMap: Record<
      string,
      {
        type: 'picture' | 'img';
        name: string;
        alt: string;
        aspectRatio?: string;
      }
    > = {
      '01': { type: 'picture', name: 'message-01', alt: 'Message 01', aspectRatio: '283/70' },
      '02': { type: 'picture', name: 'human-01', alt: 'Human 01', aspectRatio: '91/101' },
      '03': { type: 'img', name: 'icon-01', alt: 'Icon 01' },
      '04': { type: 'img', name: 'icon-02', alt: 'Icon 02' },
      '05': { type: 'picture', name: 'human-02', alt: 'Human 02', aspectRatio: '91/101' },
      '06': { type: 'picture', name: 'message-02', alt: 'Message 02', aspectRatio: '27/7' },
      '07': { type: 'picture', name: 'message-03', alt: 'Message 03', aspectRatio: '29/6' },
      '08': { type: 'picture', name: 'message-04', alt: 'Message 04', aspectRatio: '23/13' },
      '09': { type: 'picture', name: 'human-03', alt: 'Human 03', aspectRatio: '91/101' },
      '10': { type: 'img', name: 'icon-03', alt: 'Icon 03' },
    };

    return imageMap[id] || null;
  }

  private setupObserver() {
    const container = this.el.querySelector(`.${COMPONENT_NAME}__container`);
    if (!container) return;

    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isVisible) {
            this.isVisible = true;
            this.animateItems();
            this.observer?.unobserve(entry.target);
          }
        });
      },
      {
        threshold: this.getThreshold(),
        rootMargin: this.getRootMargin(),
      },
    );

    this.observer.observe(container);
  }

  private animateItems() {
    const itemElements = this.el.querySelectorAll(`.${COMPONENT_NAME}__item`);
    const items = this.getItems();

    itemElements.forEach(itemEl => {
      const itemId = itemEl.getAttribute('data-id');
      const itemConfig = items.find(item => item.id === itemId) || {};

      const animationType = itemConfig.animationType || 'fade-up';
      const animationDelay = itemConfig.animationDelay || 0;
      const animationDuration = itemConfig.animationDuration || 1000;

      // Set CSS custom properties for animation
      itemEl.setAttribute('data-animation-type', animationType);
      const existingStyle = itemEl.getAttribute('style') || '';
      const newStyle = existingStyle
        ? `${existingStyle}; --animation-delay: ${animationDelay}ms; --animation-duration: ${animationDuration}ms;`
        : `--animation-delay: ${animationDelay}ms; --animation-duration: ${animationDuration}ms;`;
      itemEl.setAttribute('style', newStyle);

      // Add visible class after delay
      if (Build.isBrowser) {
        const timeout = window.setTimeout(() => {
          itemEl.classList.add('is-visible');
        }, animationDelay);

        this.animationTimeouts.push(timeout);
      }
    });
  }

  private renderItem(item: any) {
    const imageConfig = this.getImageConfig(item.id);
    if (!imageConfig) {
      return null;
    }

    const itemId = item.id;
    const animationType = item.animationType || 'fade-up';
    const animationDelay = item.animationDelay || 0;
    const animationDuration = item.animationDuration || 1000;

    if (imageConfig.type === 'img') {
      return (
        <me-picture
          key={itemId}
          data-id={itemId}
          src={`${BASE_IMAGE_URL}/${imageConfig.name}.svg`}
          alt={imageConfig.alt}
          aspectRatio="1/1"
          lazy={true}
          class={`${COMPONENT_NAME}__item`}
          data-animation-type={animationType}
          style={
            {
              '--animation-delay': `${animationDelay}ms`,
              '--animation-duration': `${animationDuration}ms`,
            } as any
          }
        />
      );
    }

    // me-picture element
    const imageName = imageConfig.name;
    return (
      <me-picture
        key={itemId}
        data-id={itemId}
        src={`${BASE_IMAGE_URL}/${imageName}.png`}
        webpSrcSet={`${BASE_IMAGE_URL}/${imageName}.webp 1x, ${BASE_IMAGE_URL}/${imageName}@2x.webp 2x`}
        srcSet={`${BASE_IMAGE_URL}/${imageName}.png 1x, ${BASE_IMAGE_URL}/${imageName}@2x.png 2x`}
        alt={imageConfig.alt}
        aspectRatio={imageConfig.aspectRatio}
        class={`${COMPONENT_NAME}__item`}
        data-animation-type={animationType}
        style={
          {
            '--animation-delay': `${animationDelay}ms`,
            '--animation-duration': `${animationDuration}ms`,
          } as any
        }
      />
    );
  }

  render() {
    const items = this.getItems();

    if (!items || items.length === 0) {
      return (
        <Host data-color-mode={this.colorMode}>
          <div class={`${COMPONENT_NAME}__container`}></div>
        </Host>
      );
    }

    return (
      <Host data-color-mode={this.colorMode}>
        <div class={`${COMPONENT_NAME}__container`}>{items.map(item => this.renderItem(item))}</div>
      </Host>
    );
  }
}
